import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jobDescriptionAPI } from '../services/api';

// Deschide PDF-ul fișei postului într-un tab nou (preview inline). Folosește fetch + blob
// ca să atașeze Authorization (endpoint-ul cere auth).
const openJobDescriptionPdf = async (instanceId) => {
  try {
    const token = localStorage.getItem('authToken');
    const res = await fetch(jobDescriptionAPI.getPdfUrl(instanceId), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (_) {
    /* silent */
  }
};

// JobDescriptionPendingList — fișele postului pending pentru semnătura admin/SSM curent.
// Controlled component: items + selecția sunt gestionate de părinte (EmployeeSheets) pentru
// a permite integrarea în „Semnare în bulk" unificată (un singur prompt de parolă pentru
// fișe + teste + admiteri + fișa postului).
//
// Props:
//   items          — lista de pending fetched de părinte din /job-descriptions/pending-for-me
//   selectedIds    — Set<instanceId> selectate (din state parent)
//   onToggle(id)   — toggle selecție pe un item
//   bulkMode       — true dacă suntem în „mod selectare bulk" global; arată checkbox-uri.
const JobDescriptionPendingList = ({ items = [], selectedIds, onToggle, bulkMode }) => {
  const { t } = useTranslation();
  // Restrânsă implicit, la fel ca secțiunea „Teste de semnat" — se aduna prea mult conținut
  // sub lista de angajați. Userul o deschide din header.
  const [expanded, setExpanded] = useState(false);

  if (!items || items.length === 0) return null;

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm ring-1 ring-amber-200 dark:ring-amber-900/50 p-4 mb-4">
      {/* Header clickabil — toggle expandare (model identic cu cardul „Teste de semnat"). */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 flex-wrap text-left"
      >
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <svg
            className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="font-bold text-slate-900 dark:text-slate-100">
            {t('jobDescription.pendingListTitle', {
              count: items.length,
              defaultValue: `Fișe ale postului de semnat (${items.length})`,
            })}
          </h3>
          {!expanded && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {t('jobDescription.collapsedHint', 'click pentru a extinde')}
            </span>
          )}
          {bulkMode && expanded && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {t(
                'jobDescription.bulkModeHint',
                'Bifează cele de semnat; vor fi incluse în semnarea bulk de mai sus.'
              )}
            </span>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {expanded && (
        <div className="space-y-2 mt-3">
          {items.map((item) => {
            const isSelected = selectedIds && selectedIds.has(item.instanceId);
            return (
              <div
                key={item.instanceId}
                className={`flex items-center gap-3 p-3 rounded-lg border transition ${
                  isSelected
                    ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700'
                    : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'
                }`}
              >
                {bulkMode && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggle && onToggle(item.instanceId)}
                    className="w-4 h-4 rounded text-amber-600 focus:ring-amber-500 flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 dark:text-slate-100 truncate">
                    {item.employeeName}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {item.companyName} ·{' '}
                    {t('jobDescription.domain', {
                      domain: item.domain,
                      defaultValue: `Domeniu: ${item.domain}`,
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => openJobDescriptionPdf(item.instanceId)}
                    title={t('jobDescription.viewPdf', 'Vezi PDF-ul fișei postului')}
                    aria-label={t('jobDescription.viewPdf', 'Vezi PDF-ul fișei postului')}
                    className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-200 dark:text-slate-300 dark:hover:text-slate-100 dark:hover:bg-slate-700 rounded-lg transition"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  </button>
                  <Link to={`/job-descriptions/${item.instanceId}/sign`}>
                    <button className="px-4 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm rounded-lg font-medium whitespace-nowrap transition">
                      {t('jobDescription.signNow', 'Semnează acum')}
                    </button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default JobDescriptionPendingList;
